import { Injectable, NgZone } from '@angular/core'
import {
    BehaviorSubject,
    Observable,
    catchError,
    mergeWith,
    of,
    switchMap,
    throttleTime,
    Subscription,
    tap,
    ReplaySubject,
    filter,
} from 'rxjs'
import { BookingService } from '@services/booking.service'
import { BookingStatusType } from '@app/domain/BookingStatus'
import { BookingStatusTypeDTO } from '@services/DTO/BookingStatusDTO'
import { ContextService } from '@services/context.service'
import { NumberPublisher } from '@app/domain/NumberPublisher'
import { Booking } from '@app/domain/Booking'
import { SseService } from '@services/sse.service'
import { DateTime } from 'luxon'

@Injectable({
    providedIn: 'root',
})
export class RequestedBookingsCountPublisher implements NumberPublisher {

    private number = new BehaviorSubject<number>(0)
    public number$ = this.number.asObservable()
    private refreshRateSeconds = 60 * 5
    private pollingSubscription: Subscription | null = null
    private refreshTrigger$ = new ReplaySubject<void>(1)

    constructor(
        private bookingService: BookingService,
        private contextService: ContextService,
        private sseService: SseService,
        private zone: NgZone
    ) { }

    startPollingForRequestedBookings() {
        if (this.pollingSubscription) {
            return
        }
        this.pollingSubscription = this.zone.runOutsideAngular(() => this.makePollForRequestedBookings().subscribe())
        this.refreshNow()
    }

    refreshNow() {
        this.refreshTrigger$.next()
    }

    private makePollForRequestedBookings() {
        const sseBookingCreated$ = this.sseService.events$.pipe(
            filter(event => event.eventType === 'booking_created'),
        )
        const sseStatusChanges$ = this.sseService.events$.pipe(
            filter(event =>
                event.eventType === 'booking_status_changed'
                && event.previousStatusType === BookingStatusTypeDTO.Requested
                && (event.currentStatusType === BookingStatusTypeDTO.Booked || event.currentStatusType === BookingStatusTypeDTO.Rejected)
            ),
        )
        return sseBookingCreated$
            .pipe(
                mergeWith(sseStatusChanges$),
                mergeWith(this.emitPeriodically()),
                mergeWith(this.refreshTrigger$),
                throttleTime(1000, undefined, { leading: true, trailing: true }),
                switchMap(() => this.getRequestedBookingsInDateRange()),
                catchError(() => {
                    return of([])
                }),
                tap(bookings => {
                    this.zone.run(() => this.number.next(bookings.length))
                })
            )
    }

    private emitPeriodically() {
        return new Observable<void>((observer) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null
            const emitFunction = () => {
                observer.next(undefined)
                timeoutId = setTimeout(emitFunction, this.refreshRateSeconds * 1000)
            }
            emitFunction()
            return () => {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId)
                }
            }
        })
    }

    private getRequestedBookingsInDateRange(): Observable<Booking[]> {
        const organisation = this.contextService.getOrganisation()
        const business = this.contextService.getBusiness()
        const venue = this.contextService.getVenue()
        if (organisation === null || business === null || venue === null) {
            return of([])
        }
        const maximumInAdvanceDays = organisation.maximumInAdvanceDaysInVenue(venue) ?? 7
        const fromDate = DateTime.now()
            .setZone(venue.timeZone)
            .startOf('day')
            .toJSDate()
        const toDate = DateTime.now()
            .setZone(venue.timeZone)
            .endOf('day')
            .plus({ days: maximumInAdvanceDays })
            .toJSDate()
        return this.bookingService.getBookings(
            business.id,
            venue.id,
            fromDate,
            toDate,
            [BookingStatusType.Requested]
        )
    }
}
